import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskPriorityRequest, TaskPriorityResponse } from '../../../models/task.model';
import { slugifyToCode } from '../../../utils/slugify-code.util';
import { ToastService } from '../../../../../shared/services/toast.service';

@Component({
  selector: 'app-task-priorities-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './task-priorities-form.component.html',
})
export class TaskPrioritiesFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TaskPriorityResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  private nameManuallyEdited = false;

  readonly LEVEL_OPTIONS = [
    { value: 1, label: 'Urgente', color: '#EF4444', description: 'Atención inmediata' },
    { value: 2, label: 'Alta',    color: '#F97316', description: 'Importante, plazo corto' },
    { value: 3, label: 'Media',   color: '#3B82F6', description: 'Estándar' },
    { value: 4, label: 'Baja',    color: '#9CA3AF', description: 'Sin urgencia' },
  ];

  readonly COLOR_PRESETS = [
    '#EF4444', '#F97316', '#F59E0B', '#EAB308',
    '#84CC16', '#22C55E', '#10B981', '#3B82F6',
    '#6366F1', '#8B5CF6', '#A855F7', '#9CA3AF',
  ];

  constructor(private fb: FormBuilder, private api: TaskCatalogApiService, private toast: ToastService) {
    this.initForm();
  }

  ngOnInit(): void {}

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      label: ['', [Validators.required, Validators.maxLength(50)]],
      color: ['#F44336', [Validators.maxLength(30)]],
      level: [3, [Validators.required, Validators.min(1), Validators.max(4)]],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.nameManuallyEdited = false;
    this.initForm();
  }

  loadItem(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.itemId = id;
    this.nameManuallyEdited = true;
    this.api.getPriorityById(id).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          label: item.label,
          color: item.color ?? '#F44336',
          level: item.level ?? 3,
          isActive: item.isActive ?? true,
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la prioridad';
        this.loading = false;
      },
    });
  }

  onLabelInput(value: string): void {
    if (!this.nameManuallyEdited) {
      this.form.patchValue({ name: slugifyToCode(value) }, { emitEvent: false });
    }
  }
  onNameInput(): void { this.nameManuallyEdited = true; }
  setColor(c: string): void { this.form.patchValue({ color: c }); }
  setLevel(l: number): void { this.form.patchValue({ level: l }); }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: TaskPriorityRequest = {
      name: v.name.trim().toUpperCase(),
      label: v.label.trim(),
      color: v.color || undefined,
      level: Number(v.level),
      isActive: v.isActive ?? true,
    };

    const req$ = this.isEditing && this.itemId
      ? this.api.updatePriority(this.itemId, body)
      : this.api.createPriority(body);

    req$.subscribe({
      next: (item) => { this.saving = false; this.saved.emit(item); },
      error: (err) => {
        this.saving = false;
        // 409 = orden (level) ya usado por otra prioridad activa. Mostramos el mensaje del backend (Incidente 19).
        const msg = err.error?.message || 'Error al guardar la prioridad de tarea';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  cancelar(): void { this.cancelled.emit(); }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    if (c.errors['min'] || c.errors['max']) return 'Debe ser entre 1 y 4';
    return 'Campo inválido';
  }
}
